RivalIQ.module('graph', function (graph, App, Backbone, Marionette, $, _, d3) {

    'use strict';

    const labelPosition = RivalIQ.enums.graph.position;

    graph.StackedBar = graph.Block.mixin({
        className: 'stacked-bar',
        ticks: 3,
        drawEmptyLine: false,

        mixins: {
            CalculatedMargin: graph.mixins.CalculatedMargin
        },

        margin: {
            top: 20,
            right: 10,
            bottom: 30
        },

        initialize: function (options) {
            const me = this;
            me.yTicks = options.yTicks || me.yTicks;
            me.data = me.processData(options.data);
        },

        // A scale function that puts ticks at the edges of graphs
        // and in the middle of bars not at the edges
        labelScale: function (domain) {
            const me = this;

            return function (i) {
                switch (me.getTickPosition(domain, i)) {
                    case labelPosition.LEFT:
                        return 0;
                    case labelPosition.RIGHT:
                        return me.dimensions.width;
                    case labelPosition.MIDDLE:
                        return me.x(i) + me.x.rangeBand() / 2;
                    default:
                        throw new Error(`unknown tick position ${me.getTickPosition(domain, i)}`);
                }
            };
        },

        _drawBars: function (svg, data, metrics) {
            const me = this;

            const colors = metrics.invoke('getColor');
            const classNames = metrics.invoke('getClassName');
            const customClassNames = me.getOption('classNames');

            const period = svg.selectAll('.period')
                .data(data);

            period
                .call(me.animate, function (nodes) {
                    nodes.attr('transform', function (d) { return `translate(${me.x(d[0].x)},0)`; });
                }, !me.isResizing);

            period
                .enter()
                .append('g')
                .attr('class', 'period')
                .attr('transform', function (d) {
                    return `translate(${me.x(d[0].x)},0)`;
                });

            period
                .exit()
                .remove();

            graph.utils.createTooltip(period, me.getOption('tooltip'));

            const rect = period.selectAll('rect')
                .data(function (d) { return d; });

            const classNameFn = function (d, i) {
                let polarityClassName = null;
                if (d.value < 0 && me.isPlusMinusGraph()) {
                    polarityClassName = 'insta-lost-followers';
                }

                return _.compact([
                    polarityClassName,
                    customClassNames.apply(me, arguments),
                    classNames[i]
                ]).join(' ');
            };

            rect
                .call(me.animate, function (nodes) {
                    nodes
                        .attr('class', classNameFn)
                        .attr('fill', function (d, i) { return colors[i]; })
                        .attr('width', me.x.rangeBand())
                        .attr('y', function (d) { return Math.min(me.y(d.y0), me.y(d.y1)); })
                        .attr('height', function (d) { return Math.abs(me.y(d.y0) - me.y(d.y1)); });
                });

            rect
                .enter()
                .append('rect')
                .attr('class', classNameFn)
                .attr('fill', function (d, i) { return colors[i]; })
                .attr('width', me.x.rangeBand())
                .attr('y', function (d) { return Math.min(me.y(d.y1), me.y(d.y0)); })
                .attr('height', function (d) { return Math.abs(me.y(d.y0) - me.y(d.y1)); });
            rect
                .exit()
                .remove();
        },

        isPlusMinusGraph: function () {
            const me = this;

            return Boolean(me.getOption('showPlusMinusGraph'));
        },

        onlyPlusDataArrays: function (dataArrays) {
            const me = this;

            if (!me.isPlusMinusGraph()) {
                return dataArrays;
            }

            const metrics = me.getOption('metrics');

            if (_.size(metrics) > 1) {
                return _.filter(dataArrays, function (data, idx) {
                    const metric = metrics.at(idx);
                    return !metric.get('isNegativeMetric');
                });
            }

            return _.map(dataArrays, function (dataArray) {
                return _.reduce(dataArray, function (memo, data) {
                    let datum = [].concat(data);
                    if (_.last(data) < 0) {
                        datum = [].concat(_.initial(data), null);
                    }
                    return memo.concat([datum]);
                }, []);
            });
        },

        onlyNegativeDataArrays: function (dataArrays) {
            const me = this;

            if (!me.isPlusMinusGraph()) {
                return [];
            }

            const metrics = me.getOption('metrics');
            if (_.size(metrics) > 1) {
                return _.filter(dataArrays, function (data, idx) {
                    const metric = metrics.at(idx);
                    return metric.get('isNegativeMetric');
                });
            }

            return _.map(dataArrays, function (dataArray) {
                return _.reduce(dataArray, function (memo, data) {
                    let datum = [].concat(data);
                    if (_.last(data) >= 0) {
                        datum = [].concat(_.initial(data), null);
                    }
                    return memo.concat([datum]);
                }, []);
            });
        },

        _computeYMinFromData: function (data) {
            const me = this;

            const dataType = me.getMetricDataType();

            if (me.isPlusMinusGraph()) {
                data = me.onlyNegativeDataArrays(data || me.getOption('data'));
                // NOTE we need to filter out `null`, as it will erroneously report
                // as zero, and could therefore provide the wrong maximum
                const filteredData = _.filter(_.zip.apply(null, data), function (datum) {
                    return _.isFinite(_.last(_.last(datum)));
                });

                // This is going to compute the sum of the max for the negative data arrays:
                // yMin = SUM(max(negativeMetrics))
                const max = d3.max(filteredData, function (row) {
                    return d3.sum(row, function (values) {
                        return Math.abs(_.last(values));
                    });
                });

                if (dataType !== RivalIQ.enums.dataType.INTEGER) {
                    return (max || 1);
                }

                return -_.max([max, me.yTicks]);
            } else {
                data = data || me.getOption('data');
                // NOTE we need to filter out `null`, as it will erroneously report
                // as zero, and could therefore provide the wrong maximum
                data = _.filter(_.zip.apply(null, data), function (datum) {
                    return _.isFinite(_.last(_.last(datum)));
                });

                const min = d3.min(data, function (row) {
                    return d3.min(row, function (values) {
                        return _.last(values);
                    });
                });

                if (!_.isFinite(min) || min > 0) {
                    return 0;
                } else {
                    return min;
                }
            }
        },

        _computeYMaxFromData: function (data) {
            const me = this;
            const dataType = me.getMetricDataType();

            data = me.onlyPlusDataArrays(data || me.getOption('data'));

            const max = d3.max(_.zip.apply(null, data), function (row) {
                return d3.sum(row, _.last);
            });

            if (dataType !== RivalIQ.enums.dataType.INTEGER) {
                return (max || 1);
            }

            return _.max([max, me.yTicks]);
        },

        getYMin: function (data) {
            const me = this;

            if (!me.isPlusMinusGraph()) {
                return me._computeYMinFromData(data);
            }

            return -_.max([me._computeYMaxFromData(data), -me._computeYMinFromData(data), me.yTicks]);
        },

        getYMax: function (data) {
            const me = this;

            if (!me.isPlusMinusGraph()) {
                return me._computeYMaxFromData(data);
            }

            return _.max([me._computeYMaxFromData(data), -me._computeYMinFromData(data), me.yTicks]);
        },

        _drawAnnotations: function (svg, annotationData, data) {
            const me = this;

            annotationData = _.map(annotationData, function (annotation) {
                const annotationY = _.maxBy(data[annotation.x], 'y1')?.y1 ?? 0;
                return _.extend({
                    y0: annotationY
                }, annotation);
            });

            const annotations = svg.selectAll('.annotation')
                .data(annotationData);

            annotations
                .attr('transform', function (d) {
                    return `translate(${me.x(d.x) + me.x.rangeBand() / 2}, ${me.y(d.y0)})`;
                });

            annotations
                .enter()
                .append('g')
                .attr('class', 'annotation')
                .attr('transform', function (d) {
                    return `translate(${me.x(d.x) + me.x.rangeBand() / 2}, ${me.y(d.y0)})`;
                })
                .append('text')
                .text(function (d) { return d.icon; })
                .attr('dx', '-0.5em')
                .attr('dy', '-0.5em');

            annotations
                .exit()
                .remove();

            graph.utils.createTooltip(annotations, {
                content: function (d) { return d.information; }
            });
        },

        _drawEmpty: function (svg, data) {
            const me = this;

            const line = d3.svg.line()
                .x(function (d) {
                    return d;
                })
                .y(me.dimensions.height);

            const path = svg.selectAll('.line')
                .data(data);

            path
                .enter().append('path')
                .attr('class', 'line empty-stroke');

            path
                .attr('d', line);

            path
                .exit().remove();
        },

        _getDataForGrouping: function (data) {
            const me = this;
            const metrics = me.getOption('metrics');
            if (_.size(metrics) > 1) {
                return _.reduce(data, function (arr, column, index) {
                    const metric = metrics.at(index);
                    const isNegativeMetric = metric.get('isNegativeMetric');

                    return arr.concat(_.map(column, function (itm, i) {
                        // Sum all previous metrics that are of the same pos/neg type

                        const value = _.last(itm);
                        let offset;
                        if (isNegativeMetric) {

                            offset = _.reduce(_.range(0, index), function (sum, j) {
                                if (isNegativeMetric === metrics.at(j).get('isNegativeMetric')) {
                                    return sum + _.last(data[j][i]);
                                } else {
                                    return sum;
                                }
                            }, 0);
                            return {
                                x: _.head(itm),
                                y0: -offset - value,
                                y1: -offset,
                                mIndex: index,
                                value: value
                            };
                        } else {
                            offset = _.reduce(_.range(0, index), function (sum, j) {
                                const dataValue = _.last(data[j][i]);
                                if (Math.sign(value) === Math.sign(dataValue)) {
                                    return sum + dataValue;
                                } else {
                                    return sum;
                                }
                            }, 0);

                            return {
                                x: _.head(itm),
                                y0: offset,
                                y1: offset + value,
                                mIndex: index,
                                value: value
                            };
                        }
                    }));
                }, []);
            }

            return _.reduce(data, function (memo, datum, idx) {
                return memo.concat(_.map(datum, function (day) {
                    const value = _.last(day);

                    const options = {};
                    if (value < 0) {
                        options.y0 = value;
                        options.y1 = -0;
                    } else {
                        options.y0 = 0;
                        options.y1 = value;
                    }

                    return _.extend({
                        x: _.head(day),
                        mIndex: idx,
                        value: value
                    }, options);
                }));
            }, []);
        },

        _groupData: function (data) {
            const me = this;
            return _.values(_.groupBy(me._getDataForGrouping(data), 'x'));
        },

        renderData: function (svg, data) {
            const me = this;
            const dimensions = me.dimensions;
            const metric = me.getOption('concept');
            const metrics = me.getOption('metrics');

            me.x = d3.scale.ordinal()
                .rangeBands([0, dimensions.width], 0.2, 0)
                .domain(me.getDomain(metric));

            me.y = d3.scale.linear()
                .range([dimensions.height, 0])
                .domain([me.getYMin(data), me.getYMax(data)])
                .nice(me.yTicks);

            me._drawTicks(svg, data, metrics.first());
            data = me._groupData(data);

            me._drawBars(svg, data, metrics);

            if (me.hasAnyBars(data)) {
                me._drawAnnotations(svg, me.getOption('annotations'), data);
                me._drawEmpty(svg, []);
            } else {
                me._drawAnnotations(svg, [], data);

                if (me.getOption('drawEmptyLine')) {
                    me._drawEmpty(svg, [[0, me.dimensions.width]]);
                }
            }
        },

        hasAnyBars: function (data) {
            return _.some(data, function (datum) {
                return _.some(datum, ({y1}) => Boolean(y1));
            });
        },

        onBeforeDestroy: function () {
            const me = this;

            me.$('.annotation').popover('destroy');
            me.$('.period').popover('destroy');
        }
    });

    graph.StackedBar.reloadable = true;

}, window.d3);
